// Escalation policy editor. Admin-only.
//
// Two editing surfaces: the name (an inline input that saves on blur via
// the Save button), and the steps (a list of cards, each with a delay
// input and a multi-select of org members).
//
// Why local steps state rather than reading directly from snapshot: the
// step editor needs to support add/remove/reorder mid-edit without a
// snapshot reset wiping uncommitted changes. Snapshot is the source of
// truth on initial load and after a successful save.

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { onSnapshot, query, orderBy } from 'firebase/firestore';
import {
  ArrowLeft,
  ArrowDown,
  ArrowUp,
  Calendar,
  Save,
  Trash2,
  Plus,
  User as UserIcon,
  Users,
  UserPlus,
  Workflow,
  X,
} from 'lucide-react';
import { useOrg } from '../contexts/OrgContext';
import {
  orgEscalationPolicyDoc,
  orgMembersCol,
  orgSchedulesCol,
  orgTeamsCol,
} from '../lib/firestore';
import {
  deleteEscalationPolicy,
  readPolicy,
  updateEscalationPolicy,
} from '../lib/escalations';
import type {
  EscalationPolicy,
  EscalationStep,
  EscalationTarget,
  EscalationTargetType,
  OrgMember,
  Schedule,
  Team,
} from '../lib/types';
import {
  AppShell,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  Container,
  FormField,
  Input,
  PageHeader,
  Row,
  Spinner,
  Stack,
} from '../components/ui';
import { ErrorBanner } from './SigninPage';

export default function EscalationPolicyDetailPage() {
  const { id: policyId } = useParams<{ id: string }>();
  const { orgId } = useOrg();
  const navigate = useNavigate();

  const [policy, setPolicy] = useState<EscalationPolicy | null | 'missing'>(null);
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);

  // Local edit state — seeded once per snapshot identity (changing
  // policyId or a save-driven snapshot resets it).
  const [name, setName] = useState('');
  const [steps, setSteps] = useState<EscalationStep[]>([]);
  const [seedKey, setSeedKey] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Subscribe to the policy doc.
  useEffect(() => {
    if (!orgId || !policyId) return;
    const unsub = onSnapshot(
      orgEscalationPolicyDoc(orgId, policyId),
      (snap) => {
        if (!snap.exists()) {
          setPolicy('missing');
          return;
        }
        const p = readPolicy(snap.data() as Parameters<typeof readPolicy>[0]);
        setPolicy(p);
        // Reseed the editor when the snapshot is for a *different* save —
        // we use updatedAt as the identity because content changes there.
        const key = `${policyId}:${p.updatedAt}`;
        if (seedKey !== key) {
          setName(p.name);
          setSteps(p.steps);
          setSeedKey(key);
        }
      },
      (err) => {
        console.error('[escalation-policy] snapshot failed', err);
        setError('Could not load policy.');
      },
    );
    return () => unsub();
  }, [orgId, policyId, seedKey]);

  // Subscribe to org members, teams, and schedules for the target
  // selector — all three can be picked as escalation targets, with
  // different semantics (user = page that person; team = fan-out;
  // schedule = page the current on-call).
  useEffect(() => {
    if (!orgId) return;
    const unsubMembers = onSnapshot(
      query(orgMembersCol(orgId), orderBy('joinedAt', 'asc')),
      (snap) => {
        setMembers(
          snap.docs.map((d) => {
            const raw = d.data() as Omit<OrgMember, 'joinedAt'> & {
              joinedAt: number | { toMillis(): number };
            };
            return {
              ...raw,
              joinedAt:
                typeof raw.joinedAt === 'number' ? raw.joinedAt : raw.joinedAt.toMillis(),
            };
          }),
        );
      },
    );
    const unsubTeams = onSnapshot(
      query(orgTeamsCol(orgId), orderBy('name', 'asc')),
      (snap) => setTeams(snap.docs.map((d) => d.data() as Team)),
    );
    const unsubSchedules = onSnapshot(
      query(orgSchedulesCol(orgId), orderBy('name', 'asc')),
      (snap) => setSchedules(snap.docs.map((d) => d.data() as Schedule)),
    );
    return () => {
      unsubMembers();
      unsubTeams();
      unsubSchedules();
    };
  }, [orgId]);

  const dirty = useMemo(() => {
    if (!policy || policy === 'missing') return false;
    if (name.trim() !== policy.name) return true;
    if (steps.length !== policy.steps.length) return true;
    return steps.some((s, i) => {
      const orig = policy.steps[i];
      if (s.delayMinutes !== orig.delayMinutes) return true;
      if (s.targets.length !== orig.targets.length) return true;
      // Match by composite (type, id) key — same entity in different
      // target types is treated as different.
      const origKeys = new Set(orig.targets.map((t) => `${t.type}:${t.id}`));
      return s.targets.some((t) => !origKeys.has(`${t.type}:${t.id}`));
    });
  }, [name, steps, policy]);

  if (!orgId || !policyId) return null;

  if (policy === null) {
    return (
      <AppShell>
        <Container>
          <Row gap={2} align="center">
            <Spinner /> <span className="ds-text-muted">Loading…</span>
          </Row>
        </Container>
      </AppShell>
    );
  }

  if (policy === 'missing') {
    return (
      <AppShell>
        <Container>
          <Stack gap={4}>
            <ErrorBanner message="Policy not found. It may have been deleted." />
            <Link to="/escalation-policies" className="ds-button ds-button--secondary">
              <ArrowLeft size={14} strokeWidth={2} />
              Back to policies
            </Link>
          </Stack>
        </Container>
      </AppShell>
    );
  }

  function addStep() {
    setSteps((prev) => [
      ...prev,
      { delayMinutes: prev.length === 0 ? 0 : 5, targets: [] },
    ]);
  }
  function removeStep(i: number) {
    setSteps((prev) => prev.filter((_, idx) => idx !== i));
  }
  function moveStep(i: number, dir: -1 | 1) {
    setSteps((prev) => {
      const next = [...prev];
      const j = i + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }
  function setStepDelay(i: number, value: number) {
    setSteps((prev) => prev.map((s, idx) => (idx === i ? { ...s, delayMinutes: value } : s)));
  }
  function addTarget(stepIdx: number, target: EscalationTarget) {
    setSteps((prev) =>
      prev.map((s, idx) => {
        if (idx !== stepIdx) return s;
        // Dedupe by (type, id) so the same entity can't be added
        // twice on one step, but different types with the same id
        // (unlikely but possible) won't collide.
        if (
          s.targets.some((t) => t.type === target.type && t.id === target.id)
        ) {
          return s;
        }
        return { ...s, targets: [...s.targets, target] };
      }),
    );
  }
  function removeTarget(stepIdx: number, target: EscalationTarget) {
    setSteps((prev) =>
      prev.map((s, idx) =>
        idx !== stepIdx
          ? s
          : {
              ...s,
              targets: s.targets.filter(
                (t) => !(t.type === target.type && t.id === target.id),
              ),
            },
      ),
    );
  }

  async function save() {
    if (saving || !dirty) return;
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      // Server-side validation will reject empty/invalid steps; we send
      // the raw shape and let it surface meaningful errors.
      await updateEscalationPolicy(orgId!, policyId!, {
        name: name.trim(),
        steps,
      });
      setInfo('Saved.');
    } catch (err) {
      console.error('[escalation-policy] update failed', err);
      setError(extractMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (deleting) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteEscalationPolicy(orgId!, policyId!);
      navigate('/escalation-policies', { replace: true });
    } catch (err) {
      console.error('[escalation-policy] delete failed', err);
      setError(extractMessage(err));
      setDeleting(false);
      setConfirmDeleteOpen(false);
    }
  }

  return (
    <AppShell>
      <Container>
        <Stack gap={2}>
          <Link
            to="/escalation-policies"
            className="ds-text-sm ds-text-muted"
            style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <ArrowLeft size={14} strokeWidth={2} />
            All policies
          </Link>
        </Stack>

        <PageHeader
          title={policy.name}
          subtitle={
            <Row gap={2} align="center">
              <Workflow size={14} strokeWidth={1.75} color="var(--color-text-secondary)" />
              <span className="ds-text-muted ds-text-sm">
                {steps.length} {steps.length === 1 ? 'step' : 'steps'}
              </span>
            </Row>
          }
          actions={
            <Button
              variant="primary"
              onClick={save}
              disabled={!dirty}
              loading={saving}
              iconLeft={!saving ? <Save size={14} strokeWidth={2} /> : undefined}
            >
              Save
            </Button>
          }
        />

        {error && (
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <ErrorBanner message={error} />
          </div>
        )}
        {info && (
          <div
            style={{
              marginBottom: 'var(--space-4)',
              background: 'var(--color-success-soft)',
              border: '1px solid rgba(34, 197, 94, 0.2)',
              color: 'var(--color-success-text)',
              padding: 'var(--space-3) var(--space-4)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--fs-sm)',
            }}
          >
            {info}
          </div>
        )}

        <Stack gap={6}>
          <Card>
            <CardHeader>
              <h2 style={{ fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)' }}>
                Details
              </h2>
            </CardHeader>
            <CardBody>
              <FormField label="Name">
                {(id) => (
                  <Input
                    id={id}
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={100}
                  />
                )}
              </FormField>
            </CardBody>
          </Card>

          <Stack gap={3}>
            <Row gap={2} align="center" justify="between">
              <Row gap={2} align="center">
                <h2 style={{ fontSize: 'var(--fs-lg)' }}>Steps</h2>
                <span className="ds-text-tertiary ds-text-sm">
                  Targets are notified in order. Each step waits its delay before firing.
                </span>
              </Row>
              <Button
                variant="secondary"
                size="sm"
                onClick={addStep}
                iconLeft={<Plus size={12} strokeWidth={2} />}
              >
                Add step
              </Button>
            </Row>

            {steps.length === 0 ? (
              <Card>
                <CardBody>
                  <p className="ds-text-muted">
                    No steps. Add one to define who&apos;s notified when this policy fires.
                  </p>
                </CardBody>
              </Card>
            ) : (
              <Stack gap={3}>
                {steps.map((step, i) => (
                  <StepCard
                    key={i}
                    index={i}
                    total={steps.length}
                    step={step}
                    members={members}
                    teams={teams}
                    schedules={schedules}
                    onDelay={(v) => setStepDelay(i, v)}
                    onAddTarget={(t) => addTarget(i, t)}
                    onRemoveTarget={(t) => removeTarget(i, t)}
                    onMove={(dir) => moveStep(i, dir)}
                    onRemove={() => removeStep(i)}
                  />
                ))}
              </Stack>
            )}
          </Stack>

          <Card>
            <CardHeader>
              <Row gap={2} align="center">
                <h2
                  style={{
                    fontSize: 'var(--fs-md)',
                    fontWeight: 'var(--fw-semibold)',
                    color: 'var(--color-danger-text)',
                  }}
                >
                  Danger zone
                </h2>
              </Row>
            </CardHeader>
            <CardBody>
              <Row gap={4} align="center" justify="between">
                <Stack gap={1}>
                  <span className="ds-text-sm" style={{ fontWeight: 'var(--fw-medium)' }}>
                    Delete this policy
                  </span>
                  <span className="ds-text-tertiary ds-text-xs">
                    Services that reference it will need to be updated.
                  </span>
                </Stack>
                <Button
                  variant="danger"
                  iconLeft={<Trash2 size={14} strokeWidth={2} />}
                  onClick={() => setConfirmDeleteOpen(true)}
                  disabled={deleting}
                >
                  Delete
                </Button>
              </Row>
            </CardBody>
          </Card>
        </Stack>

        <ConfirmDialog
          open={confirmDeleteOpen}
          onConfirm={remove}
          onCancel={() => setConfirmDeleteOpen(false)}
          title={`Delete ${policy.name}?`}
          confirmLabel="Delete policy"
          destructive
          loading={deleting}
        >
          <p>This permanently removes the escalation policy.</p>
          <p>This cannot be undone.</p>
        </ConfirmDialog>
      </Container>
    </AppShell>
  );
}

function StepCard({
  index,
  total,
  step,
  members,
  teams,
  schedules,
  onDelay,
  onAddTarget,
  onRemoveTarget,
  onMove,
  onRemove,
}: {
  index: number;
  total: number;
  step: EscalationStep;
  members: OrgMember[];
  teams: Team[];
  schedules: Schedule[];
  onDelay: (v: number) => void;
  onAddTarget: (t: EscalationTarget) => void;
  onRemoveTarget: (t: EscalationTarget) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}) {
  // Type-aware picker state: choose the target type first, then pick
  // an entity of that type. Closed by default.
  const [pickerType, setPickerType] = useState<EscalationTargetType | null>(null);

  // Existing targets indexed by composite (type, id) key — used to
  // dedupe the picker's options.
  const existingKeys = new Set(step.targets.map((t) => `${t.type}:${t.id}`));
  const availableUsers = members.filter(
    (m) => !existingKeys.has(`user:${m.userId}`),
  );
  const availableTeams = teams.filter(
    (t) => !existingKeys.has(`team:${t.id}`),
  );
  const availableSchedules = schedules.filter(
    (s) => !existingKeys.has(`schedule:${s.id}`),
  );

  return (
    <Card>
      <CardBody>
        <Stack gap={4}>
          <Row gap={3} align="center" justify="between">
            <Row gap={2} align="center">
              <Badge tone="accent">Step {index + 1}</Badge>
              {index === 0 ? (
                <span className="ds-text-tertiary ds-text-xs">Fires when the incident is raised</span>
              ) : (
                <span className="ds-text-tertiary ds-text-xs">
                  Fires after waiting from the previous step
                </span>
              )}
            </Row>
            <Row gap={1}>
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                disabled={index === 0}
                onClick={() => onMove(-1)}
                aria-label="Move step up"
              >
                <ArrowUp size={14} strokeWidth={2} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                disabled={index === total - 1}
                onClick={() => onMove(1)}
                aria-label="Move step down"
              >
                <ArrowDown size={14} strokeWidth={2} />
              </Button>
              <Button
                variant="danger-ghost"
                size="sm"
                onClick={onRemove}
                disabled={total <= 1}
                aria-label="Remove step"
                title={
                  total <= 1
                    ? 'A policy needs at least one step — add another before deleting this one'
                    : undefined
                }
                iconLeft={<Trash2 size={12} strokeWidth={2} />}
              >
                Delete step
              </Button>
            </Row>
          </Row>

          <Row gap={4} align="center">
            <FormField label="Delay (minutes)" hint={index === 0 ? '0 = immediate' : undefined}>
              {(id) => (
                <Input
                  id={id}
                  type="number"
                  min={0}
                  max={1440}
                  value={step.delayMinutes}
                  onChange={(e) => onDelay(Number(e.target.value || 0))}
                  style={{ width: 120 }}
                />
              )}
            </FormField>
          </Row>

          <Stack gap={2}>
            <span className="ds-label">Targets</span>
            {step.targets.length === 0 ? (
              <p className="ds-text-tertiary ds-text-sm">No targets yet — add at least one.</p>
            ) : (
              <div
                style={{
                  display: 'flex',
                  gap: 'var(--space-2)',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                }}
              >
                {step.targets.map((t) => (
                  <span
                    key={`${t.type}:${t.id}`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 'var(--space-2)',
                      background: 'var(--color-accent-soft)',
                      color: 'var(--color-accent-soft-text)',
                      padding: '4px 4px 4px 8px',
                      borderRadius: 'var(--radius-full)',
                      fontSize: 'var(--fs-xs)',
                    }}
                  >
                    {targetIcon(t.type)}
                    <span>{t.displayName ?? t.id}</span>
                    <span style={{ opacity: 0.65, fontSize: 10 }}>
                      · {targetTypeLabel(t.type)}
                    </span>
                    <button
                      type="button"
                      onClick={() => onRemoveTarget(t)}
                      aria-label={`Remove ${t.displayName ?? t.id}`}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 18,
                        height: 18,
                        borderRadius: 'var(--radius-full)',
                        background: 'rgba(0,0,0,0.2)',
                        color: 'inherit',
                      }}
                    >
                      <X size={10} strokeWidth={2.5} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Type-aware picker: choose User / Team / Schedule first,
                then a corresponding entity dropdown lists the options
                still available for this step. */}
            {pickerType === null ? (
              <Row gap={2} align="center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPickerType('user')}
                  iconLeft={<UserPlus size={12} strokeWidth={2} />}
                  disabled={availableUsers.length === 0}
                  title={
                    availableUsers.length === 0
                      ? 'Every member is already on this step'
                      : undefined
                  }
                >
                  Add user
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPickerType('team')}
                  iconLeft={<Users size={12} strokeWidth={2} />}
                  disabled={availableTeams.length === 0}
                  title={
                    availableTeams.length === 0
                      ? teams.length === 0
                        ? 'No teams yet — create one on /teams'
                        : 'Every team is already on this step'
                      : undefined
                  }
                >
                  Add team
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPickerType('schedule')}
                  iconLeft={<Calendar size={12} strokeWidth={2} />}
                  disabled={availableSchedules.length === 0}
                  title={
                    availableSchedules.length === 0
                      ? schedules.length === 0
                        ? 'No rotations yet — create one on /schedules'
                        : 'Every rotation is already on this step'
                      : undefined
                  }
                >
                  Add on-call
                </Button>
              </Row>
            ) : (
              <Row gap={2} align="center">
                <select
                  className="ds-input"
                  style={{ minWidth: 240 }}
                  value=""
                  autoFocus
                  onBlur={() => setPickerType(null)}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    if (!selectedId) {
                      setPickerType(null);
                      return;
                    }
                    if (pickerType === 'user') {
                      const m = availableUsers.find((x) => x.userId === selectedId);
                      if (m) {
                        onAddTarget({
                          type: 'user',
                          id: m.userId,
                          displayName: m.displayName?.trim() || m.email || m.userId,
                        });
                      }
                    } else if (pickerType === 'team') {
                      const t = availableTeams.find((x) => x.id === selectedId);
                      if (t) {
                        onAddTarget({ type: 'team', id: t.id, displayName: t.name });
                      }
                    } else if (pickerType === 'schedule') {
                      const s = availableSchedules.find((x) => x.id === selectedId);
                      if (s) {
                        onAddTarget({
                          type: 'schedule',
                          id: s.id,
                          displayName: s.name,
                        });
                      }
                    }
                    setPickerType(null);
                  }}
                >
                  <option value="" disabled>
                    {pickerType === 'user'
                      ? 'Pick a user…'
                      : pickerType === 'team'
                        ? 'Pick a team…'
                        : 'Pick an on-call rotation…'}
                  </option>
                  {pickerType === 'user' &&
                    availableUsers.map((m) => (
                      <option key={m.userId} value={m.userId}>
                        {m.displayName?.trim() || m.email || m.userId.slice(0, 8)}
                      </option>
                    ))}
                  {pickerType === 'team' &&
                    availableTeams.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.memberIds.length} member
                        {t.memberIds.length === 1 ? '' : 's'})
                      </option>
                    ))}
                  {pickerType === 'schedule' &&
                    availableSchedules.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                        {s.teamName ? ` (${s.teamName})` : ''}
                      </option>
                    ))}
                </select>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPickerType(null)}
                >
                  Cancel
                </Button>
              </Row>
            )}
          </Stack>
        </Stack>
      </CardBody>
    </Card>
  );
}

function targetIcon(type: EscalationTargetType) {
  switch (type) {
    case 'user':
      return <UserIcon size={11} strokeWidth={2} />;
    case 'team':
      return <Users size={11} strokeWidth={2} />;
    case 'schedule':
      return <Calendar size={11} strokeWidth={2} />;
  }
}

function targetTypeLabel(type: EscalationTargetType): string {
  switch (type) {
    case 'user':
      return 'user';
    case 'team':
      return 'team';
    case 'schedule':
      return 'on-call';
  }
}

function extractMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const m = (error as { message: unknown }).message;
    if (typeof m === 'string' && m.length > 0) return m;
  }
  return 'Something went wrong. Try again.';
}
